import { Calendar, Popover } from "antd";
import dayjs from "dayjs";
import _ from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { CalendarArrow } from "@/Components/Icons/CalendarArrow";
import { resetCandidateShiftScheduleFilter } from "@/reducers/clientcandidatedetail";

import CandidateProfileCalenderDateUi from "./CandidateProfileCalenderDateUi";
import { CandidateProfileCalenderPopups } from "./CandidateProfileCalenderPopups";
import { CandidateShiftScheduleTypesList } from "./CandidateShiftScheduleTypesList";

const CandidateProfileCalendar = () => {
  const {
    candidateCalData,
    candidateAvailabilityData,
    candidateShiftScheduleFilter,
  } = useSelector((state) => state.clientCandidateDetail);
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [activeCalender, setActiveCalender] = useState([
    dayjs(),
    dayjs().add(1, "month"),
  ]);
  const [activeMonth, setActiveMonth] = useState(0);

  const getMonthName = (monthIndex) => {
    let monthName = moment().month(monthIndex).format("MMM");
    return monthName;
  };

  // Reset filter when component did mount
  useEffect(() => {
    dispatch(resetCandidateShiftScheduleFilter());
  }, []);

  useEffect(() => {
    // Preprocess and merge candidateCalData and candidateAvailabilityData for status
    let mergedData = _.cloneDeep(candidateCalData);
    _.forEach(candidateAvailabilityData, (item) => {
      const availabilityDate = moment(item.candidate_availability_date).format(
        "YYYY-MM-DD",
      );
      let newObj = {
        ...item,
        contract_status: "Available",
      };
      if (mergedData[availabilityDate]) {
        mergedData[availabilityDate].push(newObj);
      } else {
        mergedData[availabilityDate] = [newObj];
      }
    });
    // Filter the data based on status clicked
    const filterStatus = candidateShiftScheduleFilter.shift_status;
    const filteredData = filterStatus
      ? _.mapValues(mergedData, (items) =>
          items.filter((item) => item.contract_status === filterStatus),
        )
      : mergedData;
    setData(filteredData);
  }, [
    candidateCalData,
    candidateAvailabilityData,
    candidateShiftScheduleFilter,
  ]);

  return (
    <div className="flex w-full flex-col gap-1 sm:max-lg:flex-col">
      <CandidateShiftScheduleTypesList />
      <Calendar
        fullscreen={false}
        value={activeCalender[activeMonth]}
        fullCellRender={(value) => {
          let calDataHasDate = _.has(
            data,
            moment(value.$d).format("YYYY-MM-DD"),
          );
          let isCurrentMonth = activeCalender[activeMonth].month() === value.$M;

          if (isCurrentMonth && calDataHasDate) {
            let calDateObj = data[moment(value.$d).format("YYYY-MM-DD")];

            if (!_.isArray(calDateObj)) {
              return (
                <div className="grid h-[48px] w-[48px] place-items-center">
                  <p className="h-[40px] w-[40px] text-[18px] font-medium leading-[40px] text-txtColor">
                    {value.$D}
                  </p>
                </div>
              );
            } else {
              let _calObj = {
                Available: 0,
                Applied: 0,
                Booked: 0,
              };
              calDateObj.forEach((item) => {
                let parentObj = item.contract_status;
                if (_.has(_calObj, parentObj)) {
                  _calObj[parentObj] = _calObj[parentObj] + 1;
                } else {
                  _calObj[parentObj] = 1;
                }
              });
              const bookedShiftsExist = calDateObj.some(
                (item) => item.contract_status === "Booked",
              );

              return bookedShiftsExist ? (
                <Popover
                  rootClassName="z-[1033]"
                  content={<CandidateProfileCalenderPopups data={calDateObj} />}
                >
                  <>
                    <CandidateProfileCalenderDateUi
                      _calObj={_calObj}
                      value={value}
                    />
                  </>
                </Popover>
              ) : (
                <CandidateProfileCalenderDateUi
                  _calObj={_calObj}
                  value={value}
                  calDateObj={candidateCalData}
                />
              );
            }
          } else {
            return (
              <div className="grid h-[48px] w-[48px] place-items-center">
                <p className="h-[40px] w-[40px] text-[18px] font-medium leading-[40px] text-txtColor">
                  {value.$D}
                </p>
              </div>
            );
          }
        }}
        headerRender={({ value, type, onChange, onTypeChange }) => (
          <>
            <div
              className={`relative my-[16px] grid h-[40px] grid-cols-2 overflow-hidden rounded-lg bg-grey before:absolute before:top-0 before:z-0 before:h-full before:w-1/2 before:bg-primary before:transition-all before:duration-200 before:ease-linear before:content-[''] ${activeMonth === 0 ? "before:left-0" : "before:left-1/2"}`}
            >
              <div
                className={`current_month relative z-[1] flex items-center gap-4 p-[8px_24px_8px_12px] transition-all duration-200 ease-linear ${activeMonth === 0 ? "text-white" : ""}`}
                onClick={(e) => {
                  if (
                    e.target.tagName === "BUTTON" ||
                    e.target.tagName === "svg" ||
                    e.target.tagName === "path"
                  ) {
                    return;
                  } else {
                    setActiveMonth(0);
                  }
                }}
              >
                <button
                  className="flex h-[24px] w-[24px] items-center justify-center rounded-lg !bg-white !text-txtColor"
                  onClick={() => {
                    if (activeMonth === 0) {
                      setActiveMonth(1);
                      setActiveCalender([
                        activeCalender[0].subtract(2, "month"),
                        activeCalender[1].subtract(2, "month"),
                      ]);
                    } else {
                      setActiveMonth(0);
                    }
                  }}
                >
                  <CalendarArrow />
                </button>
                <div className="line-clamp-1 grow text-[16px] font-medium leading-[normal]">
                  {getMonthName(activeCalender[0].month())}{" "}
                  {activeCalender[0].year()}
                </div>
              </div>
              <div
                className={`next_month relative z-[1] flex items-center gap-4 p-[8px_12px_8px_24px] transition-all duration-200 ease-linear ${activeMonth === 1 ? "text-white" : ""}`}
                onClick={(e) => {
                  if (
                    e.target.tagName === "BUTTON" ||
                    e.target.tagName === "svg" ||
                    e.target.tagName === "path"
                  ) {
                    return;
                  } else {
                    setActiveMonth(1);
                  }
                }}
              >
                <div className="line-clamp-1 grow text-right text-[16px] font-medium leading-[normal]">
                  {getMonthName(activeCalender[1].month())}{" "}
                  {activeCalender[1].year()}
                </div>
                <button
                  className="flex h-[24px] w-[24px] -rotate-180 items-center justify-center rounded-lg !bg-white !text-txtColor"
                  onClick={() => {
                    if (activeMonth === 1) {
                      setActiveMonth(0);
                      setActiveCalender([
                        activeCalender[0].add(2, "month"),
                        activeCalender[1].add(2, "month"),
                      ]);
                    } else {
                      setActiveMonth(1);
                    }
                  }}
                >
                  <CalendarArrow />
                </button>
              </div>
            </div>
          </>
        )}
      />
    </div>
  );
};

export default CandidateProfileCalendar;
